// App chrome: a left sidebar (sections, practice-mode access card, market clock, theme, the member)
// that collapses to icons and becomes a drawer on phones, and a slim top bar with the ⌘K stock
// search, feed status and the index strip.
import { Link } from '@/lib/nav';
import { useRouter } from '@/lib/nav';
import { useEffect, useState } from 'react';
import {
  BookOpenIcon, ChevronsLeftIcon, NotebookPenIcon, ChevronsRightIcon, CircleCheckIcon, HistoryIcon, LayoutDashboardIcon, LockIcon,
  LogOutIcon, MenuIcon, MoonIcon, SearchIcon, SettingsIcon, StarIcon, SunIcon, TrendingUpIcon, UnplugIcon, UsersIcon, XIcon,
  CrosshairIcon, SunriseIcon, type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { NSE, NSE_NAMES } from '@planb/core';
import { cn } from '@/lib/utils';
import { clockText } from '@/lib/market-clock';
import { HolidayTag } from './holiday-tag';
import { useFeatures, type FeedStatus, type IndexStripItem, type MarketClockState, type Mode } from '@/lib/client/use-market';
import { displayName, requestAccess, setTheme, useMe, useResolvedTheme } from '@/lib/client/use-me';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRecents } from '@/lib/recents';
import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ALL_SYMBOLS, disconnectKite, signOut, stockPath } from './common';
import { Change, CoinMark, Initials, KiteLogin, Logo, ModeBadge, DeskIcon } from './market-ui';
import { UserAvatar } from './user-avatar';
import { openDesk } from './desk';
import { InstallHint } from './install-hint';

export type Section = 'menu' | 'desk' | 'today' | 'flow' | 'movers' | 'plans' | 'shortlist' | 'journal' | 'settings' | 'admin' | null;

const SITE = 'https://www.moneyhq.in';
const COLLAPSE_KEY = 'mhq.sidebar';

/** The desk's coin in the brand blue, selected or not (the menu's other icons take the text colour). */
function DeskNavIcon({ className }: { className?: string }) { return <DeskIcon className={cn(className, 'text-primary')} />; }

interface NavItem { id: Exclude<Section, null>; href: string; label: string; icon: LucideIcon; live?: boolean }
const MARKET: NavItem[] = [
  { id: 'desk', href: '/desk', label: 'Desk', icon: DeskNavIcon as unknown as LucideIcon },
  { id: 'today', href: '/today', label: 'Today', icon: SunriseIcon, live: true },
  { id: 'flow', href: '/', label: 'Market Flow', icon: LayoutDashboardIcon, live: true },
  { id: 'movers', href: '/movers', label: 'Movers', icon: TrendingUpIcon, live: true },
  { id: 'plans', href: '/plans', label: 'Plans', icon: CrosshairIcon, live: true },
  { id: 'shortlist', href: '/shortlist', label: 'Shortlist', icon: StarIcon },
];

function storedCollapsed() {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
}

export function AppHeader(props: {
  section: Section; mode: Mode; status: FeedStatus | null; user: string | null;
  /** Only the admin sees the broker controls and the members list. */
  role?: 'admin' | 'member' | null;
  /** Query string carried to other pages (index, simulated session). */
  query: string;
  strip?: IndexStripItem[]; activeIndex?: string; onSelectIndex?: (id: string) => void;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [collapsed, setCollapsed] = useState(storedCollapsed);
  const locked = !!props.status?.locked;

  // ⌘K / Ctrl+K opens the stock search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setSearchOpen((x) => !x); }
      if (e.key === 'Escape') setDrawer(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  // The page content moves right by the sidebar's width (see Page and the header below).
  useEffect(() => {
    document.documentElement.style.setProperty('--sidebar-w', collapsed ? '72px' : '248px');
    try { localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0'); } catch { /* not remembered */ }
  }, [collapsed]);

  return (
    <>
      <Sidebar {...props} locked={locked} collapsed={collapsed} onCollapse={() => setCollapsed((c) => !c)}
        drawer={drawer} onClose={() => setDrawer(false)} />
      {/* Laptops: the sticky header. Phones and tablets: none (the bottom bar has search, the status
          and the menu); what the header carried sits at the top of the page instead (below). */}
      <header className="sticky top-0 z-30 border-b bg-background/90 pt-[env(safe-area-inset-top)] backdrop-blur-md backdrop-saturate-150 max-lg:hidden lg:pl-(--sidebar-w) xl:pr-(--desk-w)">
        <div className="mx-auto flex h-14 w-[min(1560px,100%-32px)] items-center gap-3">
          <button type="button" aria-label="Open menu" onClick={() => setDrawer(true)}
            className="grid size-9 place-items-center rounded-lg border bg-card text-ink lg:hidden"><MenuIcon className="size-4.5" /></button>
          <Link href={`/${props.query}`} aria-label="MoneyHQ home" className="lg:hidden"><CoinMark size={26} /></Link>
          {/* Phones: an icon; wider screens: the search box. */}
          <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search stocks"
            className="flex size-9 shrink-0 items-center justify-center gap-2.5 rounded-lg border bg-card text-sm text-muted-foreground transition-colors hover:border-primary/60 sm:w-[min(380px,100%)] sm:justify-start sm:px-3">
            <SearchIcon className="size-4" />
            <span className="hidden flex-1 truncate text-left sm:inline">Search stocks…</span>
            <kbd className="hidden rounded-md border bg-tint px-1.5 text-[11px] font-medium sm:inline">⌘K</kbd>
          </button>
          <div className="ml-auto flex min-w-0 items-center gap-2 sm:gap-2.5">
            <ModeBadge mode={props.mode} status={props.status} />
            {props.role === 'admin' && <KiteLogin status={props.status} />}
            {props.role === 'admin' && props.status?.loggedIn && (
              <DropdownMenu>
                <DropdownMenuTrigger aria-label="Broker" className="hidden h-8 items-center rounded-full border bg-card px-3 text-xs font-medium text-muted-foreground sm:inline-flex">Kite</DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel className="font-normal text-muted-foreground">Broker session {props.status.user ?? ''}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={() => void disconnectKite()}><UnplugIcon />Disconnect Kite</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {locked && <PracticeBanner />}
        {props.strip && <IndexStrip items={props.strip} active={props.activeIndex} onSelect={props.onSelectIndex} />}
      </header>
      {/* Phones and tablets: a slim top bar with the logo, the market status and search (the bottom bar
          is the tabs alone), pinned, its solid background reaching under the status bar. In the
          installed app iOS (26) blurs the strip just below the status bar (its scroll-edge effect), so
          there the bar's contents sit 12 px lower, clear of it (padding inside the bar, so its own background
          fills the space; a margin collapsed through it and showed the page behind). */}
      <div className="sticky top-0 z-30 border-b bg-background pt-[env(safe-area-inset-top)] standalone:pt-[calc(env(safe-area-inset-top)+12px)] lg:hidden">
        <div className="flex h-12 items-center gap-3 px-4">
          <Link href={`/${props.query}`} aria-label="MoneyHQ home"><Logo size={24} /></Link>
          <div className="ml-auto flex items-center gap-3">
            <StatusDot mode={props.mode} status={props.status} />
            <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search stocks"
              className="grid size-9 place-items-center rounded-full border bg-card text-muted-foreground"><SearchIcon className="size-4" /></button>
          </div>
        </div>
      </div>
      <div className="lg:hidden">
        {locked && <PracticeBanner />}
        {props.strip && <div className="border-b"><IndexStrip items={props.strip} active={props.activeIndex} onSelect={props.onSelectIndex} /></div>}
      </div>
      <StockSearch open={searchOpen} onOpenChange={setSearchOpen} query={props.query} />
      <InstallHint />
      <MobileTabs section={props.section} query={props.query} kiteDue={kiteDue(props.role, props.status)} />
    </>
  );
}

/** Phones and tablets: the bottom tab bar (More opens the menu page; the desk's button floats above). */
/** The admin's Kite session has lapsed: on phones the login sits on the menu page, and More is marked. */
export const kiteDue = (role: 'admin' | 'member' | null | undefined, status: FeedStatus | null | undefined) =>
  role === 'admin' && !!status && !status.offline && status.brokerConfigured && !status.loggedIn;

function MobileTabs({ section, query, kiteDue: due }: { section: Section; query: string; kiteDue?: boolean }) {
  const tab = (active: boolean) => cn('flex h-14 flex-col items-center justify-center gap-0.5 text-[11px] font-medium', active ? 'text-brand-strong' : 'text-muted-foreground');
  // The desk in the middle: its page where it's switched on for the user, else the panel (Journal is under More).
  const me = useMe();
  const feat = useFeatures();
  const deskPage = feat.on('desk_page') && !!me?.access;
  const links: { id: Exclude<Section, null>; href: string; label: string; icon: LucideIcon }[] = [
    { id: 'today', href: '/today', label: 'Today', icon: SunriseIcon },
    { id: 'flow', href: `/${query}`, label: 'Market', icon: LayoutDashboardIcon },
    { id: 'desk', href: '/desk', label: 'Desk', icon: DeskNavIcon as unknown as LucideIcon },
    { id: 'plans', href: `/plans${query}`, label: 'Plans', icon: CrosshairIcon },
  ];
  return (
    <nav aria-label="Main" className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md lg:hidden">
      <div className="mx-auto grid max-w-lg grid-cols-5">
        {links.map((l) => (l.id === 'desk' && !deskPage
          ? <button key={l.id} type="button" className={tab(false)} onClick={() => openDesk()}><l.icon className="size-5" />{l.label}</button>
          : (
            <Link key={l.id} href={l.href} className={tab(section === l.id)} aria-current={section === l.id ? 'page' : undefined}>
              <l.icon className="size-5" />{l.label}
            </Link>
          )))}
        <Link href={`/menu${query}`} className={tab(section === 'menu')} aria-current={section === 'menu' ? 'page' : undefined}><span className="relative"><MenuIcon className="size-5" />{due && <i className="absolute -top-0.5 -right-1 size-2 rounded-full bg-orange-500 ring-2 ring-background" aria-label="Kite login needed" />}</span>More</Link>
      </div>
    </nav>
  );
}

function Sidebar(props: {
  section: Section; query: string; role?: 'admin' | 'member' | null; locked: boolean;
  status?: FeedStatus | null;
  collapsed: boolean; onCollapse: () => void; drawer: boolean; onClose: () => void;
}) {
  const c = props.collapsed;
  return (
    <>
      {props.drawer && <div className="fixed inset-0 z-40 bg-ink/40 lg:hidden" onClick={props.onClose} aria-hidden />}
      <aside aria-label="Main"
        className={cn('fixed inset-y-0 left-0 z-50 flex w-[264px] flex-col overflow-y-auto border-r bg-card lg:overflow-visible transition-transform lg:z-40 lg:w-(--sidebar-w) lg:translate-x-0',
          props.drawer ? 'translate-x-0' : '-translate-x-full')}>
        <div className={cn('flex h-[57px] flex-none items-center gap-2 border-b px-4', c && 'lg:justify-center lg:px-0')}>
          <Link href={`/${props.query}`} aria-label="MoneyHQ home" className={cn(c && 'lg:hidden')}><Logo size={31} /></Link>
          {c && <Link href={`/${props.query}`} aria-label="MoneyHQ home" className="hidden lg:block"><CoinMark size={26} /></Link>}
          <button type="button" onClick={props.onCollapse} aria-label={c ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn('ml-auto hidden size-7 place-items-center rounded-md text-muted-foreground hover:bg-tint hover:text-ink lg:grid', c && 'lg:ml-0 lg:hidden')}>
            <ChevronsLeftIcon className="size-4" />
          </button>
          <button type="button" onClick={props.onClose} aria-label="Close menu" className="ml-auto grid size-8 place-items-center rounded-md text-muted-foreground lg:hidden"><XIcon className="size-4" /></button>
        </div>
        <SidebarBody {...props} />
      </aside>
    </>
  );
}

/** The sidebar's list (sections, the market clock and theme, Members and Settings, the member and
 * sign-out): in the sidebar and drawer, and on the phone's menu page. */
export function SidebarBody(props: {
  /** The phone's menu page: the member and sign-out are shown at its top instead. */
  page?: boolean;
  section: Section; query: string; role?: 'admin' | 'member' | null; locked: boolean;
  status?: FeedStatus | null;
  collapsed: boolean; onCollapse: () => void; drawer: boolean; onClose: () => void;
}) {
  const me = useMe();
  const feat = useFeatures();
  const theme = useResolvedTheme();
  const [confirmOut, setConfirmOut] = useState(false);
  const c = props.collapsed;
  const item = (n: { id: string; href: string; label: string; icon: LucideIcon; external?: boolean; lock?: boolean }) => {
    const active = props.section === n.id;
    const cls = cn('group flex h-10 items-center gap-3 rounded-lg px-3 text-[14px] font-medium text-muted-foreground transition-colors hover:bg-tint hover:text-ink',
      active && 'bg-brand-soft text-brand-strong hover:bg-brand-soft hover:text-brand-strong', c && 'lg:justify-center lg:px-0');
    const body = (
      <>
        <n.icon className="size-[18px] flex-none" />
        <span className={cn('flex-1 truncate', c && 'lg:hidden')}>{n.label}</span>
        {n.lock && <HistoryIcon className={cn('size-3.5 text-muted-foreground/70', c && 'lg:hidden')} aria-label="Last closed session" />}
      </>
    );
    return n.external
      ? <a key={n.id} href={n.href} target="_blank" rel="noreferrer" className={cls} title={c ? n.label : undefined}>{body}</a>
      : <Link key={n.id} href={n.href} className={cls} aria-current={active ? 'page' : undefined} title={c ? n.label : undefined} onClick={props.onClose}>{body}</Link>;
  };
  const group = (label: string) => <div className={cn('px-3 pt-5 pb-1.5 text-[11px] font-semibold tracking-[0.08em] text-muted-foreground/80 uppercase', c && 'lg:hidden')}>{label}</div>;

  return (
    <>
        <nav className="flex-1 overflow-y-auto px-3 pb-3">
          {group('Market')}
          {MARKET.filter((n) => n.id !== 'desk' || (feat.on('desk_page') && me?.access)).map((n) => item({ ...n, href: n.id === 'desk' ? n.href : `${n.href}${props.query}`, lock: props.locked && n.live }))}
          {group('Grow')}
          {item({ id: 'journal', href: `/journal${props.query}`, label: 'Journal', icon: NotebookPenIcon })}
          {item({ id: 'learn', href: `${SITE}/learn/`, label: 'Learn', icon: BookOpenIcon, external: true })}
          {c && (
            <button type="button" onClick={props.onCollapse} aria-label="Expand sidebar"
              className="mt-2 hidden h-10 w-full place-items-center rounded-lg text-muted-foreground hover:bg-tint hover:text-ink lg:grid">
              <ChevronsRightIcon className="size-4" />
            </button>
          )}
        </nav>
        <div className="flex-none space-y-3 border-t p-3">
          {props.locked && <div className={cn(c && 'lg:hidden')}><AccessCard /></div>}
          <div className={cn('flex items-center justify-between', c && 'lg:hidden')}>
            <MarketClock clock={props.status?.clock} session={props.status?.date} />
            <button type="button" onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-label={theme === 'dark' ? 'Light theme' : 'Dark theme'}
              className="grid size-8 place-items-center rounded-full border bg-card text-muted-foreground hover:text-ink">
              {theme === 'dark' ? <SunIcon className="size-4" /> : <MoonIcon className="size-4" />}
            </button>
          </div>
          <div className="grid gap-0.5">
            {props.role === 'admin' && item({ id: 'admin', href: `/admin${props.query}`, label: 'Members', icon: UsersIcon })}
            {item({ id: 'settings', href: `/settings${props.query}`, label: 'Settings', icon: SettingsIcon })}
          </div>
          {!props.page && <div className={cn('flex items-center gap-1.5 border-t pt-3', c && 'lg:flex-col lg:border-t-0 lg:pt-0')}>
            <Link href={`/settings${props.query}`} onClick={props.onClose} title={me?.user}
              className={cn('flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1.5 hover:bg-tint', c && 'lg:flex-none lg:p-0')}>
              <UserAvatar me={me} size="sm" />
              <span className={cn('min-w-0 flex-1', c && 'lg:hidden')}>
                <span className="block truncate text-[13.5px] font-semibold text-ink">{displayName(me) || 'Signed in'}</span>
                <span className="block truncate text-[11.5px] text-muted-foreground">
                  {props.role === 'admin' ? 'Admin' : props.locked ? 'Awaiting approval' : 'Member'}
                </span>
              </span>
            </Link>
            <button type="button" onClick={() => setConfirmOut(true)} aria-label="Sign out" title="Sign out"
              className="grid size-9 flex-none place-items-center rounded-lg text-muted-foreground hover:bg-bear-soft hover:text-bear">
              <LogOutIcon className="size-[18px]" />
            </button>
          </div>}
        </div>
        <Dialog open={confirmOut} onOpenChange={setConfirmOut}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Sign out?</DialogTitle>
              <DialogDescription>You&apos;ll need to sign in with Google again on this device.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmOut(false)}>Cancel</Button>
              <Button variant="destructive" onClick={() => void signOut()}><LogOutIcon />Sign out</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
    </>
  );
}

/** Pending members: what they can see before approval, and the request-access button. */
function AccessCard() {
  const me = useMe();
  const [busy, setBusy] = useState(false);
  const asked = !!me?.accessRequestedAt;
  return (
    <div className="rounded-xl border bg-tint p-3">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-ink"><LockIcon className="size-3.5 text-primary" />Live data locked</div>
      <p className="mt-1 text-[12px] leading-snug text-muted-foreground">You&apos;re seeing the last closed session, read-only. Live prices, the desk and paper trades open once your access is approved.</p>
      {asked ? (
        <p className="mt-2 flex items-center gap-1.5 text-[12px] font-medium text-bull-strong"><CircleCheckIcon className="size-3.5" />Requested · the team has been notified</p>
      ) : (
        <Button size="sm" className="mt-2 h-8 w-full rounded-full" disabled={busy}
          onClick={async () => { setBusy(true); const ok = await requestAccess(); setBusy(false); toast[ok ? 'success' : 'error'](ok ? 'Request sent' : 'Could not send the request'); }}>
          Request access
        </Button>
      )}
    </div>
  );
}

function PracticeBanner() {
  return (
    <div className="border-t bg-brand-soft/60">
      <p className="mx-auto flex min-h-9 w-[min(1560px,100%-32px)] items-center gap-2 py-1.5 text-[12.5px] text-brand-strong">
        <HistoryIcon className="size-3.5 flex-none" />
        <span><b>Last closed session.</b> You&apos;re seeing real NSE data from the most recent finished session, read-only, so you can learn every tool. Live prices, the desk and paper trades open once your access is approved.</span>
      </p>
    </div>
  );
}

/** "Open · closes 15:30", "Holiday · Dussehra · opens tomorrow", "Closed · opens Mon" (IST). Before the
 * first status arrives it guesses from the weekday and hour. */
function MarketClock({ clock, session }: { clock?: MarketClockState; session?: string | null }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 30_000); return () => clearInterval(id); }, []);
  const ist = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const min = ist.getHours() * 60 + ist.getMinutes(), wd = ist.getDay();
  const weekday = wd >= 1 && wd <= 5;
  const close = NSE.openMinute + NSE.bars * NSE.barMinutes;
  const open = clock ? clock.open : false;
  if (clock?.state === 'holiday') {
    return (
      <span className="inline-flex h-8 items-center gap-1.5 rounded-full border bg-card px-2.5 text-[12px] font-medium text-ink">
        <i className="size-1.5 rounded-full bg-muted-foreground/50" /><HolidayTag clock={clock} session={session} />
      </span>
    );
  }
  // Before the server's calendar arrives, a weekday's hours could be a holiday: never guess "open".
  const text = clock ? clockText(clock, now)
    : weekday && min >= NSE.openMinute && min < close ? 'Checking…' : weekday && min < NSE.openMinute ? 'Opens 09:15' : wd >= 1 && wd <= 4 ? 'Closed · opens 09:15' : 'Closed · opens Mon';
  return (
    <span className="inline-flex h-8 items-center gap-1.5 rounded-full border bg-card px-2.5 text-[12px] font-medium text-ink">
      <i className={cn('size-1.5 rounded-full', open ? 'bg-bull' : 'bg-muted-foreground/50')} />{text}
    </span>
  );
}

function StockSearch({ open, onOpenChange, query }: { open: boolean; onOpenChange: (o: boolean) => void; query: string }) {
  const router = useRouter();
  const recents = useRecents().map((r) => r.symbol).filter((s) => NSE_NAMES[s] || ALL_SYMBOLS.includes(s)).slice(0, 5);
  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} title="Search stocks" description="Find a stock by symbol or company name" className="sm:max-w-lg">
      <Command>
      <CommandInput placeholder="Symbol or company, e.g. TCS or Infosys" />
      <CommandList className="max-h-96">
        <CommandEmpty>No stock found.</CommandEmpty>
        {recents.length > 0 && (
          <CommandGroup heading="Recently viewed">
            {recents.map((s) => (
              <CommandItem key={'recent-' + s} value={`${s} ${NSE_NAMES[s] ?? ''} ·recent`}
                onSelect={() => { onOpenChange(false); router.push(stockPath(s, query)); }}>
                <HistoryIcon className="size-4 text-muted-foreground" />
                <span className="font-medium text-ink">{s}</span>
                <span className="truncate text-muted-foreground">{NSE_NAMES[s] ?? ''}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Stocks">
          {ALL_SYMBOLS.map((s) => (
            <CommandItem key={s} value={`${s} ${NSE_NAMES[s] ?? ''}`}
              onSelect={() => { onOpenChange(false); router.push(stockPath(s, query)); }}>
              <Initials symbol={s} size="sm" />
              <span className="font-medium text-ink">{s}</span>
              <span className="truncate text-muted-foreground">{NSE_NAMES[s] ?? ''}</span>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      </Command>
    </CommandDialog>
  );
}

export function IndexStrip({ items, active, onSelect }: { items: IndexStripItem[]; active?: string; onSelect?: (id: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="border-t bg-card">
      <div className="mx-auto flex h-10 w-[min(1560px,100%-32px)] items-center gap-7 overflow-x-auto [scrollbar-width:none]">
        {items.map((q) => (
          <a key={q.id} href={`/?index=${q.id}`}
            onClick={(e) => { if (onSelect) { e.preventDefault(); onSelect(q.id); } }}
            title={q.ltp === null ? 'Simulated: average move of the index members' : 'Live index value'}
            className="inline-flex items-baseline gap-2 text-[13px] whitespace-nowrap">
            <b className={cn('font-semibold text-ink', q.id === active && 'text-brand-strong')}>{q.name}</b>
            {q.ltp !== null && <span className="num text-[12.5px] text-ink">{q.ltp.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>}
            <Change pct={q.pct} className="text-[12.5px]" />
          </a>
        ))}
      </div>
    </div>
  );
}

/** Page width container beside the sidebar. */
export function Page({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className="lg:pl-(--sidebar-w) xl:pr-(--desk-w)">
      {/* Phones: room at the bottom for the tab bar and the home bar. */}
      <main className={cn('mx-auto w-[min(1560px,100%-32px)] pt-6 max-sm:pt-3 pb-[calc(6.5rem+env(safe-area-inset-bottom))] lg:pb-16', className)}>{children}</main>
    </div>
  );
}

/** Phones: the market status in a word (the header's badge, compact). */
function StatusDot({ mode, status }: { mode: Mode; status: FeedStatus | null }) {
  // Closed on a holiday says so (the full words, with when it opens, are in the title).
  const ck = status?.clock;
  if (mode === 'closed' && ck?.state === 'holiday') {
    return (
      <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-muted-foreground">
        <span className="size-2 rounded-full bg-muted-foreground/60" /><HolidayTag clock={ck} session={status?.date} />
      </span>
    );
  }
  const [text, cls] = mode === 'live' ? [status?.ticker === 'connected' ? 'Live' : 'Live…', 'bg-bull']
    : mode === 'closed' ? [ck?.state === 'special' ? 'Muhurat' : 'Closed', 'bg-muted-foreground/60']
      : mode === 'loading' ? ['Loading', 'bg-brand-strong'] : ['Practice', 'bg-warn'];
  return (
    <span className="flex shrink-0 items-center gap-1.5 text-[12px] font-medium text-muted-foreground" title={ck ? clockText(ck, new Date()) : text}>
      <span className={cn('size-2 rounded-full', cls, mode === 'live' && 'animate-pulse')} />{text}
    </span>
  );
}
